import React from 'react';
import { motion } from 'framer-motion';
import { Server, Plus, CheckCircle, AlertTriangle, Activity, Eye } from 'lucide-react';

interface ProviderStatus {
  provider: string;
  status: 'healthy' | 'warning' | 'error';
  zones: number;
  lastCheck: string;
}

interface ProviderStatusProps {
  providerStatuses: ProviderStatus[];
}

const ProviderStatusCard: React.FC<ProviderStatusProps> = ({ providerStatuses }) => {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'error':
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
      default:
        return <Activity className="w-5 h-5 text-gray-400" />;
    }
  };

  return (
    <motion.div
      className="card"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.4 }}
    >
      <div className="card-header">
        <div className="flex items-center justify-between">
          <h2 className="text-heading-1 text-slate-900 dark:text-white">Provider Status</h2>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
            <span className="text-body-small text-slate-500 dark:text-slate-400">Live</span>
          </div>
        </div>
      </div>
      <div className="card-body">
        {providerStatuses.length === 0 ? (
          <motion.div
            className="text-center py-12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <Server className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-heading-2 text-slate-900 dark:text-white mb-2">
              No providers configured
            </h3>
            <p className="text-body text-slate-500 dark:text-slate-400 mb-4">
              Get started by adding your first DNS provider
            </p>
            <button className="inline-flex items-center justify-center px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 font-medium rounded-lg transition-colors focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Provider
            </button>
          </motion.div>
        ) : (
          <div className="space-y-3">
            {providerStatuses.map((provider, index) => (
              <motion.div
                key={`${provider.provider}-${provider.lastCheck || index}`}
                className="flex items-center justify-between p-4 rounded-xl bg-slate-50/50 dark:bg-slate-800/50 hover:bg-slate-100/50 dark:hover:bg-slate-700/50 transition-all duration-200 group cursor-pointer"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                whileHover={{ x: 2 }}
              >
                <div className="flex items-center space-x-3">
                  <div className="relative">
                    {getStatusIcon(provider.status)}
                    <motion.div
                      className="absolute -inset-1 rounded-full opacity-0 group-hover:opacity-100"
                      animate={{ scale: provider.status === 'healthy' ? [1, 1.2, 1] : 1 }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <div className="w-full h-full bg-current opacity-20 rounded-full" />
                    </motion.div>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                      {provider.provider}
                    </p>
                    <p className="text-body-small text-slate-500 dark:text-slate-400">
                      {provider.zones} zones • Last check:{' '}
                      {new Date(provider.lastCheck).toLocaleTimeString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <span
                    className={`status ${
                      provider.status === 'healthy'
                        ? 'status-healthy'
                        : provider.status === 'warning'
                          ? 'status-warning'
                          : 'status-error'
                    }`}
                  >
                    {provider.status}
                  </span>
                  <Eye className="w-4 h-4 text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-300 transition-colors" />
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default ProviderStatusCard;
